<template>
  <div class="app-footer" :class="{'app-footer--mobile': deviceType === 'mobile'}">
    <!-- About Section -->
    <div class="app-footer__about">
      <div class="app-footer__logo-container">
        <img :src="domainConfig?.['siteIcon'] || require('@/assets/logo.png')" alt="Logo"
          class="app-footer__logo">
        <div class="app-footer__brand">{{domainConfig?.['siteName']}}</div>
      </div>
      <div class="app-footer__desc"
        v-html="domainConfig?.['ABOUTUS'] || 'We are a platform dedicated to bringing innovative AI-generated stories to life, offering an immersive experience for readers and writers alike. Join us for unique, evolving narratives.'">
      </div>
    </div>

    <!-- Resources Section -->
    <div class="app-footer__section">
      <div class="app-footer__section-title">Resources</div>
      <div class="app-footer__links">
        <router-link :to="getFooterLink('/terms-of-service')" class="app-footer__link">
          Terms of Service
        </router-link>
        <router-link :to="getFooterLink('/privacy-policy')" class="app-footer__link">
          Privacy Policy
        </router-link>
        <router-link :to="getFooterLink('/disclaimer')" class="app-footer__link">
          Disclaimer
        </router-link>
        <router-link :to="getFooterLink('/about-us')" class="app-footer__link">
          About Us
        </router-link>
      </div>
    </div>

    <!-- Contact Section -->
    <div class="app-footer__section">
      <div class="app-footer__section-title">Contact us</div>
      <div class="app-footer__links">
        <a class="app-footer__link"
          :href="`mailto:${domainConfig['IAMEMAIL'] || 'timeincities.support@gmail.com'}`">
          {{ domainConfig?.['IAMEMAIL'] || 'timeincities.support@gmail.com' }}
        </a>
      </div>
    </div>

    <!-- Social Icons -->
    <div class="app-footer__social-icons">
      <a @click="showToast" class="app-footer__social-link">
        <img src="@/assets/default/tiktok-icon.svg" alt="TikTok" class="app-footer__social-icon">
      </a>
      <a @click="showToast" class="app-footer__social-link">
        <img src="@/assets/default/x-icon.svg" alt="Twitter" class="app-footer__social-icon">
      </a>
      <a @click="showToast" class="app-footer__social-link">
        <img src="@/assets/default/facebook-icon.svg" alt="Facebook"
          class="app-footer__social-icon">
      </a>
      <a @click="showToast" class="app-footer__social-link">
        <img src="@/assets/default/instagram-icon.svg" alt="Instagram"
          class="app-footer__social-icon">
      </a>
    </div>

    <!-- Copyright -->
    <div class="app-footer__copyright-container">
      <div class="app-footer__copyright">
        Copyright © 2024 {{domainConfig?.['siteName'] || 'Sitename'}}. All Rights Reserved.
      </div>
    </div>

    <!-- Toast 提示 -->
    <div class="app-footer__toast" v-if="showToastMessage">
      <div class="app-footer__toast-content">
        Link Copied
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  name: 'AppFooter',
  data () {
    return {
      showToastMessage: false,
      toastTimer: null
    }
  },
  computed: {
    ...mapGetters({
      deviceType: 'deviceType',
      domainConfig: 'domainConfig'
    })
  },
  methods: {
    // 获取页脚链接（支持channel参数）
    getFooterLink (link) {
      // // 获取当前URL的查询参数
      const currentQuery = this.$route.query;
      const queryString = Object.keys(currentQuery).length > 0
        ? `?${new URLSearchParams(currentQuery).toString()}`
        : '';

      // 检查是否存在channel参数
      if (this.$route.params.channel) {
        return `/${this.$route.params.channel}${link}${queryString}`;
      }
      return `${link}${queryString}`;
    },
    showToast () {
      // 获取当前页面的URL
      const currentUrl = window.location.href;

      // 使用navigator.clipboard API复制链接到剪贴板
      try {
        // 使用现代剪贴板API
        navigator.clipboard.writeText(currentUrl)
          .then(() => {
            console.log('URL已复制到剪贴板:', currentUrl);

            // 显示Toast提示
            this.showToastMessage = true;

            // 清除之前的定时器
            if (this.toastTimer) {
              clearTimeout(this.toastTimer);
            }

            // 设置新的定时器，2秒后自动关闭
            this.toastTimer = setTimeout(() => {
              this.showToastMessage = false;
            }, 2000);
          })
          .catch(err => {
            console.error('无法复制URL:', err);
            // 剪贴板API失败时，尝试传统方法
            this.fallbackCopyToClipboard(currentUrl);
          });
      } catch (error) {
        console.error('复制过程出错:', error);
        // 如果navigator.clipboard不可用，尝试传统方法
        this.fallbackCopyToClipboard(currentUrl);
      }
    },

    // 后备的复制方法，适用于不支持clipboard API的浏览器
    fallbackCopyToClipboard (text) {
      // 创建一个临时textarea元素
      const textArea = document.createElement('textarea');
      textArea.value = text;

      // 设置样式使其不可见
      textArea.style.position = 'fixed';
      textArea.style.top = '0';
      textArea.style.left = '0';
      textArea.style.width = '2em';
      textArea.style.height = '2em';
      textArea.style.padding = '0';
      textArea.style.border = 'none';
      textArea.style.outline = 'none';
      textArea.style.boxShadow = 'none';
      textArea.style.background = 'transparent';

      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      try {
        // 执行复制命令
        const successful = document.execCommand('copy');
        if (successful) {
          console.log('使用传统方法复制URL成功');
          // 显示Toast提示
          this.showToastMessage = true;

          // 清除之前的定时器
          if (this.toastTimer) {
            clearTimeout(this.toastTimer);
          }

          // 设置新的定时器，2秒后自动关闭
          this.toastTimer = setTimeout(() => {
            this.showToastMessage = false;
          }, 2000);
        } else {
          console.error('使用传统方法复制失败');
        }
      } catch (err) {
        console.error('复制出错:', err);
      }

      // 清理临时元素
      document.body.removeChild(textArea);
    }
  },
  beforeDestroy () {
    // 组件销毁前清除定时器
    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
    }
  }
}
</script>

<style lang="scss">
@import '~@/css/variables.scss';

.app-footer {
	background: linear-gradient(180deg, rgba(22, 19, 80, 1) 0%, rgba(8, 1, 27, 1) 100%);
	padding: rem(52) rem(180) 0;
	display: flex;
	flex-direction: column;
	gap: rem(32);
	align-items: flex-start;
	width: 100%;
	box-sizing: border-box;
	color: #fff;

	&__about {
		display: flex;
		flex-direction: column;
		gap: rem(16);
		align-items: flex-start;
	}

	&__logo-container {
		display: flex;
		flex-direction: row;
		gap: rem(16);
		align-items: center;
	}

	&__logo {
		width: rem(32);
		height: rem(32);
	}

	&__brand {
		font-family: 'Roboto-Black', sans-serif;
		font-size: rem(24);
		line-height: rem(24);
		font-weight: 900;
		text-transform: uppercase;
	}

	&__desc {
		font-family: 'Roboto-Regular', sans-serif;
		font-size: rem(12);
		line-height: rem(16);
		font-weight: 400;
		max-width: rem(1080);
	}

	&__section {
		display: flex;
		flex-direction: row;
		gap: rem(32);
		align-items: center;
	}

	&__section-title {
		font-size: rem(14);
		line-height: rem(14);
		font-weight: 300;
		text-transform: uppercase;
		width: fit-content;
		flex-shrink: 0;
	}

	&__links {
		display: flex;
		flex-direction: row;
		gap: rem(32);
		align-items: flex-start;
	}

	&__link {
		font-family: 'Roboto-Regular', sans-serif;
		font-size: rem(13);
		line-height: rem(14);
		font-weight: 400;
		color: #fff;
		text-decoration: none;
		&:hover {
			text-decoration: underline;
		}
	}

	&__social-icons {
		display: flex;
		flex-direction: row;
		gap: rem(16);
		align-items: flex-start;
	}

	&__social-link {
		cursor: pointer;
		display: block;
		width: rem(24);
		height: rem(24);
	}

	&__social-icon {
		width: 100%;
		height: 100%;
		filter: brightness(0) invert(1);
	}

	&__copyright-container {
		border-top: 1px solid rgba(255, 255, 255, 0.2);
		padding: rem(12) 0;
		width: 100%;
		margin-top: rem(20);
	}

	&__copyright {
		font-family: 'Roboto-Light', sans-serif;
		font-size: rem(12);
		line-height: rem(12);
		font-weight: 300;
	}

	&__toast {
		position: fixed;
		bottom: 50%;
		left: 50%;
		transform: translate(-50%, 50%);
		padding: rem(10) rem(20);
		background: rgba(0, 0, 0, 0.7);
		color: #fff;
		border-radius: rem(4);
		z-index: 9999;
		font-size: rem(14);
		max-width: rem(200);
		text-align: center;
	}

	// 移动端样式
	&--mobile {
		padding: rem(40) rem(16) 0;
		gap: rem(24);

		.app-footer__section {
			flex-direction: column;
			align-items: flex-start;
			gap: rem(16);
			width: 100%;
		}

		.app-footer__links {
			flex-direction: column;
			gap: rem(16);
		}

		.app-footer__copyright-container {
			padding: rem(16) 0;
		}
	}
}
</style> 